import { Field, Schema } from '@micin-jp/chicken-schema'

import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  DrawerActionTrigger,
  DrawerBackdrop,
  DrawerBody,
  DrawerCloseTrigger,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerRoot,
  DrawerTrigger,
  FieldLabel,
  FieldRoot,
  FieldSetContent,
  FieldSetLegend,
  FieldSetRoot,
  For,
  Heading,
  Radio,
  RadioGroup,
  Separator,
  Stack,
  Text,
  Textarea,
} from '@/components/ui'

const CheckboxField = ({ field }: { field: Field }) => {
  if (field.typeDef.type !== 'Checkbox') {
    throw new Error('Invalid type for CheckboxField')
  }
  return (
    <FieldSetRoot>
      <CheckboxGroup gap="0" readOnly>
        <FieldSetLegend
          px="M"
          py="SS"
          whiteSpace="pre-wrap"
          wordBreak="break-all"
        >
          {field.name}
        </FieldSetLegend>
        <FieldSetContent p="M">
          <Stack gap="2">
            <For each={field.typeDef.choices}>
              {choice => (
                <Checkbox
                  key={choice.cid}
                  value={choice.cid}
                  whiteSpace="pre-wrap"
                  wordBreak="break-all"
                >
                  {choice.name}
                </Checkbox>
              )}
            </For>
          </Stack>
        </FieldSetContent>
      </CheckboxGroup>
    </FieldSetRoot>
  )
}
const RadioForm = ({ field }: { field: Field }) => {
  if (field.typeDef.type !== 'Radio') {
    throw new Error('Invalid type for RadioForm')
  }
  return (
    <FieldSetRoot>
      <FieldSetLegend
        px="M"
        py="SS"
        whiteSpace="pre-wrap"
        wordBreak="break-all"
      >
        {field.name}
      </FieldSetLegend>
      <RadioGroup readOnly m="0">
        <FieldSetContent p="M">
          <Stack gap="2">
            <For each={field.typeDef.choices}>
              {choice => (
                <Radio
                  key={choice.cid}
                  value={choice.cid}
                  whiteSpace="pre-wrap"
                  wordBreak="break-all"
                >
                  {choice.name}
                </Radio>
              )}
            </For>
          </Stack>
        </FieldSetContent>
      </RadioGroup>
    </FieldSetRoot>
  )
}
const TextareaForm = ({ field }: { field: Field }) => {
  if (field.typeDef.type !== 'Textarea') {
    throw new Error('Invalid type for TextareaForm')
  }
  return (
    <FieldRoot gap="0">
      <FieldLabel px="M" py="SS" whiteSpace="pre-wrap" wordBreak="break-all">
        {field.name}
      </FieldLabel>
      <Box p="M" w="full">
        <Textarea readOnly />
      </Box>
    </FieldRoot>
  )
}
const LabelField = ({ field }: { field: Field }) => {
  if (field.typeDef.type !== 'Label') {
    throw new Error('Invalid type for LabelField')
  }
  return (
    <Box>
      {/* labelと同じstyleをあてる */}
      <Text
        px="M"
        py="SS"
        as="span"
        fontSize="md"
        fontWeight="semibold"
        color="gray.600"
        whiteSpace="pre-wrap"
        wordBreak="break-all"
      >
        {field.name}
      </Text>
      <Text p="M" whiteSpace="pre-wrap" wordBreak="break-all" fontSize="md">
        {field.typeDef.content}
      </Text>
    </Box>
  )
}

const FieldContent = ({ field }: { field: Field }) => {
  const { typeDef } = field

  // 表示するのはCheckbox, Radio, Labelのみ
  switch (typeDef.type) {
    case 'Checkbox':
      return <CheckboxField field={field} />
    case 'Radio':
      return <RadioForm field={field} />
    case 'Textarea':
      return <TextareaForm field={field} />
    case 'Label':
      return <LabelField field={field} />
    default:
      return null
  }
}

type Props = {
  trigger: React.ReactNode
  schema: Schema
  trialNumber: string
}

export const CriteriaViewerDrawer = ({
  trigger,
  schema,
  trialNumber,
}: Props) => {
  return (
    <DrawerRoot size="lg">
      <DrawerBackdrop />
      <DrawerTrigger asChild>{trigger}</DrawerTrigger>
      <DrawerContent>
        <DrawerHeader display="flex" gap="4" alignItems="center" p="6">
          <Heading fontSize="lg">選択除外基準</Heading>
          <Text fontSize="md">試験ID: {trialNumber}</Text>
        </DrawerHeader>
        <Separator />
        <DrawerBody>
          {schema.fields.map(field => (
            <FieldContent key={field.fid} field={field} />
          ))}
        </DrawerBody>
        <Separator />
        <DrawerFooter p="6">
          <DrawerActionTrigger>
            <Button variant="outline">閉じる</Button>
          </DrawerActionTrigger>
        </DrawerFooter>
        <DrawerCloseTrigger />
      </DrawerContent>
    </DrawerRoot>
  )
}
